import React, { useState, useEffect, useRef } from 'react';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { GoogleMap, LoadScript, useJsApiLoader } from '@react-google-maps/api';
import { RoutableRichText } from '../../react/RoutableRichText';
import { RoutableLink } from '../../react/RoutableLink';
import css from './index.module.scss';
import { stringify } from 'querystring';

const InteractiveMap = (props) => {
  const { fields, sitecoreContext } = props;
  const isEditing = sitecoreContext?.pageEditing;
  const mapSections = fields?.mapSections
  const linkListings = fields?.LinkListing;
  const accordionItems =  mapSections;

  //map init
  const containerStyle = {
    width: '100%',
    height: '598px'
  };

  const center = {
    lat: 41.7931802,
    lng: -87.6032097
  };

  const zoomLevelInt = parseInt(fields?.zoomLevel?.value);
  const zoomLevel = !isNaN(zoomLevelInt) ? zoomLevelInt : 12; //default 12

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    libraries: ["geometry"],
    googleMapsApiKey: fields?.APIKEY?.value
  });

  const [map, setMap] = useState<google.maps.Map>(null);
  const [bounds, setBounds] = useState<google.maps.LatLngBounds>(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [stops, setStops] = useState<Array<Array<google.maps.Marker>>>([]);
  const [lines, setLines] = useState<google.maps.Polyline[]>([]);
  const [linesShadow, setLinesShadow] = useState<google.maps.Polyline[]>([]);
  const [mapNotice, setMapNotice] = useState('');

  const checkboxes:HTMLInputElement[] = [];

  const onLoad = React.useCallback(function callback(map) {

    //get map center
    const lat = parseFloat(fields?.centerLat?.value);
    const lon = parseFloat(fields?.centerLon?.value);
    if(!isNaN(lat) || !isNaN(lon)) {
      center.lat = lat;
      center.lng = lon;
    }
    map.setCenter(center);
    setMap(map);
    setActiveIndex(0);
    
    //exclude POI business
    if(fields?.excludePOIBusiness?.value)
    {
      map.setOptions({ styles: [{
        featureType: "poi.business",
        stylers: [{ visibility: "off" }],
      }] });
    }

  }, []);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null)
  }, []);

  const parseNameValueField = (data:string):google.maps.LatLng[] => {
    
    let latLngs = new Array<google.maps.LatLng>();

    let sPoints = data.split('&');
    for(let i = 0;i<sPoints.length; i++){

      let rawPoint = sPoints[i].split('=');
      if(rawPoint.length > 1){
        let lat = parseFloat(rawPoint[0]);
        let lon = parseFloat(rawPoint[1]);
        if(!isNaN(lat) || !isNaN(lon)) {
          let point = new google.maps.LatLng(lat,lon);
          latLngs.push(point);
        }
      }
    }
    return latLngs;
  };

  const handleClick = (index, item) =>{
    if(activeIndex === index){
      setActiveIndex(null);
      setMapNotice('Map no longer shows '+ item.fields.title.value);
    }
    else{
      clearMarkers();
      setActiveIndex(index);
      setMapNotice('Map now shows '+ item.fields.title.value);
    }
  };


  const handleCheckBoxClick = (index, checked, routeItem) => {
    //show/hide routes and stops
    if(checked){
      lines[index]?.setMap(map);
      linesShadow[index]?.setMap(map);
      setMapOnStops(index,true);
      centerOnPolylines(lines);
      setMapNotice('Route '+ routeItem.fields.title.value + ' was added to the map');
    } else {
      lines[index]?.setMap(null);
      linesShadow[index]?.setMap(null);
      setMapOnStops(index,false);
      setMapNotice('Route '+ routeItem.fields.title.value + ' was removed from the map');
    }
  };

  const setMapOnStops = (index, enabled) => {
    //show/hide stops
    if(enabled) {
      stops[index]?.map((stop) =>{
        stop.setMap(map);
      });
    }
    else{
      stops[index]?.map((stop) =>{
        stop.setMap(null);
      });
    }
  };

  const centerOnPolylines = (polyLines) => {
    //center map on routes
    var bounds = new google.maps.LatLngBounds();
    
    polyLines.map((line,pathIndex)=> {
      if(line.getMap() != null){
        line?.getPath()?.getArray().map((path,i)=>{
          bounds.extend(path);
        });
      }
    });
    
    map.fitBounds(bounds);
  };

  //add the map iframe title after the component is rendered for a11y EWPB-778
  // for fully loaded window see: https://stackoverflow.com/questions/75441901/trigger-react-hook-after-page-is-completely-loaded
  useEffect(() => {
    
    const handleLoad = () => {
      // this block will run after the entire page has loaded
      let mapContainer = document.querySelector('.'+css.mapContainer);
      let mapIframe = mapContainer.querySelector('div > iframe');
      if (mapIframe) {
        mapIframe.setAttribute("title", "UChicago Transportation Route Map");
      }

      //insert an anchor tag above the map to address scroll issue EWPB-891
      const mapScrollDiv = document.createElement("div");
      mapScrollDiv.setAttribute("tabindex","0");
      mapScrollDiv.addEventListener('focus', function(e){if(typeof window !== "undefined"){window.scrollTo(670,676)}});
      const parent = mapContainer.parentNode;
      parent.insertBefore(mapScrollDiv, mapContainer);
    }; 

    if(typeof window !== "undefined") {
      window.addEventListener('load', handleLoad);
    }
    
    // Cleanup -> Remove the event listener on unmount
    return () => {
      if(typeof window !== "undefined") {
        window.removeEventListener('load', handleLoad);
      }
    };
  },[]);

  useEffect(() => {
    if(isLoaded){
      populateMapPoints(activeIndex);
    }
  },[activeIndex]);

  useEffect(()=> {
    //center on polyLines updates
    if(isLoaded && lines.length > 0){

      let sectionZoomLevelRaw = parseInt(accordionItems[activeIndex]?.fields?.zoomLevel?.value);
      let SectionZoomLevelSet = !isNaN(sectionZoomLevelRaw);
      let sectionZoomLevel = !isNaN(sectionZoomLevelRaw) ? sectionZoomLevelRaw : zoomLevel;
      map.setZoom(sectionZoomLevel);
      if(!SectionZoomLevelSet){
        centerOnPolylines(lines);
      }
    }
  },[lines]);

  const populateMapPoints = (index) => {
    let points = accordionItems[index]?.fields?.mapPoints;
    let routes = accordionItems[index]?.fields?.mapRoutes;
    let sectionItem = accordionItems[index]?.fields;

    //get icon

    if(points?.length > 0)
    {
      var bounds = new google.maps.LatLngBounds();
      points.map((point,index) => {

        let lat = parseFloat(point?.fields?.lat?.value);
        let lon = parseFloat(point?.fields?.lon?.value);
        if(!isNaN(lat) || !isNaN(lon)) {

          let isMarketIconSet = sectionItem?.markerIcon?.value?.src && sectionItem?.markerIcon?.value?.src?.length > 0;
          let markerIcon = sectionItem?.markerIcon?.value ? sectionItem?.markerIcon?.value?.src : "https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi3_hdpi.png";

          let newMarker = new google.maps.Marker({
            position: { lat: lat, lng: lon},
            map,
            title: point?.fields?.title?.value,
            icon: {
              url: markerIcon,
              scaledSize: isMarketIconSet ? new google.maps.Size(52,52) : new google.maps.Size(26,37)
            },
            label: {
              text: point?.fields?.title?.value,
              className: 'markerText'
            } 
          });
          //click event for accessibility
          newMarker.addListener("click", () => {});
          setMarkers( arr => [...arr, newMarker]);
          bounds.extend(newMarker.getPosition());
        }
      });
      //center map on points
      map.fitBounds(bounds);

    } else if(routes?.length > 0) {

      //check all checkboxes 
      checkboxes.map((item)=>{
        item.checked = true;
      });
      setStops([]);
      
      routes.map((routeItem,index) => {
        if(routeItem && routeItem?.fields){
          
          let routePath = routeItem?.fields?.jsonData;
          let routeStops = routeItem?.fields?.jsonDataStops;
          
          if(routePath === undefined || routePath.length == 0 && routeItem?.fields?.polylineEncodingRoute?.value?.length > 0) {
            routePath = google.maps.geometry.encoding?.decodePath(routeItem?.fields?.polylineEncodingRoute?.value);            
          }
          if(routeStops === undefined || routeStops.length == 0 && routeItem?.fields?.polylineEncodingStops?.value?.length > 0) {
            routeStops = google.maps.geometry.encoding?.decodePath(routeItem?.fields?.polylineEncodingStops?.value);
          }
          
          if(routePath === undefined || routePath.length == 0) {
            //use Name Value Field
            routePath = parseNameValueField(routeItem?.fields?.routePath?.value);
          };
          if(routeStops === undefined || routeStops.length == 0) {
            //use Name Value Field
            routeStops = parseNameValueField(routeItem?.fields?.routeStops?.value);
          };

          const routePathShadow = new google.maps.Polyline({
            path: routePath,
            geodesic: true,
            strokeColor: 'black',
            strokeOpacity: 0.25,
            strokeWeight: 7
          });

          const routePathLine = new google.maps.Polyline({
            path: routePath,
            geodesic: true,
            strokeColor: routeItem?.fields?.routeColor?.value ? routeItem?.fields?.routeColor?.value : "#FF0000",
            strokeOpacity: 0.85,
            strokeWeight: 4,
          });

          let stopMarkers:Array<google.maps.Marker> = new Array<google.maps.Marker>();
          routeStops?.map((stop)=>{

            const svgStopIcon = {
              path: "M10,19 C14.9705627,19 19,14.9705627 19,10 C19,7.47019775 17.9562261,5.18418195 16.2758375,3.54911173 C14.6545937,1.97159128 12.4407605,1 10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 Z",
              fillColor: routeItem?.fields?.routeColor?.value ? routeItem?.fields?.routeColor?.value : "#FF0000",
              fillOpacity: 0.5,
              strokeColor: "black",
              strokeOpacity: 0.8,
              strokeWeight: 1,
              rotation: 0,
              scale: 0.5,
              anchor: new google.maps.Point(10, 10),
            };

            const routeStopsDots = new google.maps.Marker({
              position: stop,
              title: "Route Stop",
              icon: svgStopIcon,
              map: map
            });
            
            stopMarkers.push(routeStopsDots);
          });
          
          setStops(arr => [...arr, stopMarkers]);
          setLinesShadow(arr => [...arr, routePathShadow]);
          setLines(arr => [...arr, routePathLine]);
          
          setMapOnStops(index,true);
          routePathShadow.setMap(map);
          routePathLine.setMap(map);
          
        }
      });
    } else {
      clearMarkers();
    }
  };

  const clearMarkers = () => {

      //clear markers
      for(let i = 0;i < markers.length;i++)
      {
        markers[i].setMap(null);
      }
      for(let i = 0;i < lines.length;i++)
      {
        lines[i].setMap(null);
      }
      for(let i = 0;i < linesShadow.length;i++)
      {
        linesShadow[i].setMap(null);
      }
      for(let i = 0;i < stops.length;i++)
      {
        setMapOnStops(i,false);
      }
      setStops([]);
      setLinesShadow([]);
      setLines([]);
      setMarkers([]);
      setBounds(new google.maps.LatLngBounds(center));
      map.fitBounds(new google.maps.LatLngBounds(center));
      map.setCenter(center);
      map.setZoom(zoomLevel);
      
  };

  


const renderedAccoridonItems = accordionItems.map((item, index) => {
    const ariaExpanded = index === activeIndex ? "true" : "false";
    const onSubmit = (event) => {
      event.preventDefault();
    };
    if (item.fields.mapRoutes) {
      return (
        <form onSubmit={onSubmit}>
          <ul>
            <MapAccordionItem
              key={index}
              ariaExpanded={isEditing ? "true" : ariaExpanded}
              item={item}
              index={index}
              onClick={() => { handleClick(index, item); }}
              onCheckBoxClick={handleCheckBoxClick}
              isEditing={isEditing}
              checkboxRef={checkboxes}
            />
          </ul>
        </form>
      )
    } else {
      return (
        <ul>
            <MapAccordionItem
              key={index}
              ariaExpanded={isEditing ? "true" : ariaExpanded}
              item={item}
              index={index}
              onClick={() => { handleClick(index, item); }}
              onCheckBoxClick={handleCheckBoxClick}
              isEditing={isEditing}
              checkboxRef={checkboxes}
            />
        </ul>
      )
    }

});



return (
  <section className={css.container}>
      <div className={css.accordionContainer}>

        {renderedAccoridonItems}
        {
          linkListings?.map((linkList,index) => (

            <div key={`linkListItem-${index}`} className={css.linkList}>
                {
                  linkList?.fields?.children?.map((link,linkIndex) => (
                    //links go here
                    <div key={`linkItem-${linkIndex}`} className={css.linkItem}>
                      {
                        (link.fields?.link?.value?.href && link.fields?.linkText?.value) &&
                        <RoutableLink field={link?.fields?.link} className={css.viewMoreLink} data-chevron={true}>
                            <Text field={link?.fields?.linkText} />
                        </RoutableLink>
                      }
                    </div>
                  ))
                 }
            </div>
          ))
        }
      </div>
      <div className={css.mapNoticeContainer} aria-live="polite">{mapNotice}</div>
      <div className={css.mapContainer}>
      {  isLoaded ? 
        <GoogleMap
          mapContainerStyle={containerStyle}
          zoom={zoomLevel}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          {}
          <></>
        </GoogleMap>
        : <></>
      }
      </div>
  </section>
);
}


//Accordion (MapSection)
const MapAccordionItem = ({
  ariaExpanded,
  item,
  index,
  onClick,
  onCheckBoxClick,
  isEditing,
  checkboxRef
}) => {
  const moreInfos:HTMLDivElement[] = [];

  const toggleMoreInfo = (event,selectedIndex) => {
    let expanded = event.currentTarget.getAttribute('data-expanded');
    if(expanded == 'true'){
      event.currentTarget.setAttribute('data-expanded','false');
      moreInfos[selectedIndex].setAttribute('data-expanded','false');
    }else{
      event.currentTarget.setAttribute('data-expanded','true');
      moreInfos[selectedIndex].setAttribute('data-expanded','true');
    }
  };

  return(
    <li key={`accordion-${index}`} className={css.accordionListItem} data-show={ariaExpanded} >
      <Text 
        className={css.button}  
        tag='button' 
        field={item?.fields?.title} 
        onClick={onClick}
        tabIndex='0' 
        role='button' 
        aria-disabled='false' 
        aria-expanded={ariaExpanded}
        aria-controls={`panel${index + 1}_content`}
      />
    <div className={css.sectionContents} id={`panel${index + 1}_content`} >


      <ul className={css.mapPointList}>
        {
          item?.fields?.mapPoints?.map((pointItem, index) => (

              <li key={`points-${index}`} className={css.mapPointItem}>
                <Text tag='div' className={css.title} field={pointItem?.fields.title}></Text>
                <div className={css.descriptionContainer}>
                <Text tag='p' className={css.description} field={pointItem?.fields.description}></Text>
                  {
                    pointItem?.fields?.moreInfo?.value &&
                    <button 
                      className={css.moreInfoToggle} 
                      onClick={(event) => {toggleMoreInfo(event,index);}}
                      aria-label={`More info: ${pointItem?.fields.title.value}`}
                    >More</button>
                  }
                  </div>
                  {
                    pointItem?.fields?.moreInfo?.value &&
                    <div className={css.moreInfo} ref={el => moreInfos[index] = el }>
                      <RoutableRichText field={pointItem?.fields?.moreInfo} />
                    </div>
                  }

              </li>
          ))
        }


        { 
          item?.fields?.mapRoutes?.map((routeItem, index) => {

            var checkboxId = routeItem.id;
            
            return(
            <li key={`points-${index}`} className={css.mapPointItem}>
              <div className={css.checkboxContainer}>
                <input id={checkboxId} type="checkbox" className={css.checkbox} ref={chk => checkboxRef[index] = chk} defaultChecked={true} onChange={(event) => { onCheckBoxClick(index, event.target.checked, routeItem); }} />
                <div className={css.checkboxDescriptionContainer}>
                  <Text tag='label' className={css.title} field={routeItem?.fields.title} for={checkboxId}></Text>
                  <div className={css.descriptionContainer}>
                  <Text tag='p' className={css.description} field={routeItem?.fields.description}></Text>
                  {
                    routeItem?.fields?.moreInfo?.value &&
                    <button 
                      className={css.moreInfoToggle} 
                      onClick={(event) => {toggleMoreInfo(event,index);}}
                      aria-label={`More info: ${routeItem?.fields.title.value}`}
                      >More</button>
                  }
                  </div>

                  {
                    routeItem?.fields?.moreInfo?.value &&
                    <div className={css.moreInfo} ref={el => moreInfos[index] = el }>
                      <RoutableRichText field={routeItem?.fields?.moreInfo} />
                    </div>
                  }
                </div>
                {
                  (isEditing && routeItem?.fields?.jsonDataError) &&
                  <div className={css.jsonError}>[{routeItem?.fields?.jsonDataError}]</div>
                }
              </div>
                
            </li>
            )
          })
        }
        
      </ul>
      
      { 
        item?.fields?.description &&
        <RoutableRichText field={item?.fields.description} className={css.description} />
      }     
      {
        (item.fields?.link?.value?.href && item.fields?.linkText?.value) &&
        <RoutableLink field={item?.fields?.link} className={css.viewMoreLink} data-chevron={true}>
            <Text field={item?.fields?.linkText} />
        </RoutableLink>
      }
    </div>
  </li>
  )
}

export default withSitecoreContext()(InteractiveMap);